import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, PageLoader } from '../../components/ui';
import { taskService } from '../../services';

export default function Calendar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchCalendarTasks = async () => {
    setLoading(true);
    try {
      const startDate = currentDate.startOf('month').subtract(7, 'day').toISOString();
      const endDate = currentDate.endOf('month').add(7, 'day').toISOString();
      const data = await taskService.getCalendarTasks({ startDate, endDate });
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'));
  const goToNextMonth = () => setCurrentDate(currentDate.add(1, 'month'));
  const goToToday = () => setCurrentDate(dayjs());

  const getCalendarDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(startOfMonth.subtract(startDay - i, 'day'));
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(currentDate.date(i));
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push(endOfMonth.add(i, 'day'));
    }

    return days;
  };

  const getTasksForDay = (day) => {
    return tasks.filter(task => {
      const dueDate = dayjs(task.dueDate);
      return dueDate.isSame(day, 'day');
    });
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarDays = getCalendarDays();

  return (
    <>
      <Helmet>
        <title>{t('nav.calendar')} - ProCTM</title>
      </Helmet>

      <PageHeader title={t('nav.calendar')} />

      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={goToPrevMonth}>
                <ChevronLeftIcon className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-semibold px-4">
                {currentDate.format('MMMM YYYY')}
              </h2>
              <Button variant="ghost" onClick={goToNextMonth}>
                <ChevronRightIcon className="w-5 h-5" />
              </Button>
            </div>
            <Button variant="outline" onClick={goToToday}>
              Today
            </Button>
          </div>

          {loading ? (
            <PageLoader />
          ) : (
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}

              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.month() === currentDate.month();
                const isToday = day.isSame(dayjs(), 'day');
                const dayTasks = getTasksForDay(day);

                return (
                  <div
                    key={index}
                    className={`bg-white min-h-24 p-2 ${!isCurrentMonth ? 'opacity-50' : ''
                      }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isToday
                        ? 'w-6 h-6 flex items-center justify-center bg-primary text-white rounded-full'
                        : ''
                      }`}>
                      {day.date()}
                    </div>
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map((task) => {
                        const taskId = task._id || task.id;
                        return (
                          <div
                            key={taskId}
                            onClick={() => taskId && navigate(`/tasks/${taskId}`)}
                            className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer transition-all hover:opacity-80 hover:shadow-sm ${task.status === 'completed'
                                ? 'bg-gray-100 text-gray-500 line-through'
                                : task.priority === 'urgent'
                                  ? 'bg-danger/10 text-danger hover:bg-danger/20'
                                  : task.priority === 'high'
                                    ? 'bg-accent/10 text-accent-700 hover:bg-accent/20'
                                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                              }`}
                            title={task.title}
                          >
                            {task.title}
                          </div>
                        );
                      })}
                      {dayTasks.length > 3 && (
                        <div
                          onClick={() => {
                            // Navigate to task list filtered by this date
                            const dateStr = day.format('YYYY-MM-DD');
                            navigate(`/tasks?dueDate=${dateStr}`);
                          }}
                          className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 hover:underline"
                          title={`View all ${dayTasks.length} tasks for this day`}
                        >
                          +{dayTasks.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
